/**
 * src/components/ui/Modal.tsx
 * -----------------------------
 * Accessible modal dialog shell used across the Customer Chat UI.
 *
 * Fully implemented this will:
 *   - Use a <dialog> element for native accessibility (focus trap, Escape key).
 *   - Apply a backdrop overlay with a click-to-close handler.
 *   - Animate open/close with framer-motion (scale + fade).
 *   - Support full-screen mode on mobile viewports (<640px).
 *   - Accept optional `title` (renders an <h2> accessible label).
 *   - Export ModalFooter sub-component for standardized action buttons.
 *   - Lock body scroll while open.
 *
 * Props:
 *   isOpen    — boolean
 *   onClose   — () => void
 *   title     — optional string
 *   children  — ReactNode
 *   className — additional wrapper classes
 *
 * Key dependencies: framer-motion
 */
import { useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icon } from '@/components/ui/Icon'
import type { ReactNode } from 'react'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  className?: string
}

export function Modal({ isOpen, onClose, title, children, className }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)

  // Lock body scroll and handle Escape key
  useEffect(() => {
    if (!isOpen) return
    document.body.style.overflow = 'hidden'
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handler)
    }
  }, [isOpen, onClose])

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="cx-modal-layer"
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
        >
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="cx-modal-backdrop"
          />

          {/* Panel */}
          <motion.div
            key="panel"
            ref={dialogRef}
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ type: 'spring', stiffness: 400, damping: 32 }}
            className={cn(
              'cx-modal-panel',
              className,
            )}
          >
            {/* Header */}
            {title && (
              <div className="cx-modal-header">
                <h2 id="modal-title">
                  {title}
                </h2>
                <button
                  onClick={onClose}
                  aria-label="Close dialog"
                  className="cx-modal-close"
                >
                  <Icon name="close" size={16} />
                </button>
              </div>
            )}

            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
